import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { trackAuthRedirect } from '@/utils/authTelemetry';
import { logError, logWarning } from '@/utils/errorHandlerUtil';
import { stringHasValue } from '@/utils/stringUtil';

const TOKEN_RETRY_DELAY_MS = 10000;

// Identifies a signed-in session by account and the app's own claims (extension_*, isForgotPassword).
// A silent refresh keeps the same key, so the stored authResult doesn't change and the API hooks
// (whose callbacks depend on authResult) don't re-run their data loads. Null when there's no token.
function getSessionKey(result: AuthenticationResult | null): string | null {
  if (result == null || !stringHasValue(result.accessToken)) return null;

  const claims = (result.account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const appClaims = Object.keys(claims)
    .filter((key) => key.startsWith('extension_') || key === 'isForgotPassword')
    .sort()
    .map((key) => [key, claims[key]]);

  return JSON.stringify([result.account?.homeAccountId ?? null, appClaims]);
}

interface TokenContextProps {
  // Stable for the session: it is only replaced when the account or app claims change, not on a
  // silent token refresh. Use getLatestAuthResult() for the current access token and expiry.
  authResult: AuthenticationResult;
  getLatestAuthResult: () => AuthenticationResult | null;
  b2cId: string | null;
  validated: boolean;
  authPca: PublicClientApplication | null;
  isTokenMissing: boolean;
  updateAccessToken: (value: React.SetStateAction<AuthenticationResult | null>) => void;
  updateB2cId: (id: string | null) => void;
  updateValidated: (info: boolean) => void;
  updateAuthPca: (info: PublicClientApplication | null) => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const TokenContext = createContext<TokenContextProps>({
  authResult: {} as AuthenticationResult,
  getLatestAuthResult: () => null,
  b2cId: null,
  validated: false,
  authPca: null,
  isTokenMissing: true,
  updateAccessToken: () => {},
  updateB2cId: () => {},
  updateValidated: () => {},
  updateAuthPca: () => {},
});

export const TokenProvider = ({ children }: ProviderProps) => {
  const navigate = useNavigate();
  const statusCheckRef = React.useRef<{
    isDisabled: boolean | undefined;
    isValidated: boolean | undefined;
  } | null>(null);

  const [fetchedAccessToken, setFetchedAccessToken] = useState<AuthenticationResult | null>(null);
  const [b2cId, setB2cId] = useState<string | null>(null);
  const [validated, setValidated] = useState<boolean>(false);
  const [authPca, setAuthPca] = useState<PublicClientApplication | null>(null);
  const latestAuthResultRef = useRef<AuthenticationResult | null>(null);

  const updateAccessToken = useCallback(
    (value: React.SetStateAction<AuthenticationResult | null>) => {
      const next = typeof value === 'function' ? value(latestAuthResultRef.current) : value;
      latestAuthResultRef.current = next;

      setFetchedAccessToken((prevValue) => {
        const prevKey = getSessionKey(prevValue);
        return prevKey != null && prevKey === getSessionKey(next) ? prevValue : next;
      });
    },
    []
  );

  const getLatestAuthResult = useCallback(() => latestAuthResultRef.current, []);

  const isTokenMissing =
    fetchedAccessToken?.accessToken === null ||
    fetchedAccessToken?.accessToken === undefined ||
    fetchedAccessToken?.accessToken === '';

  const updateAuthPca = useCallback((pca: PublicClientApplication | null) => {
    setAuthPca(pca);
  }, []);

  const updateB2cId = useCallback(
    (id: string | null) => {
      if (id != b2cId) setB2cId(id);
    },
    [b2cId]
  );

  const updateValidated = useCallback((info: boolean) => {
    setValidated(info);
  }, []);

  useEffect(() => {
    const newValidated = fetchedAccessToken?.account?.idTokenClaims?.extension_userIsValidated;
    if (newValidated != null) {
      setValidated(newValidated as boolean);
    }
  }, [fetchedAccessToken]);

  useEffect(() => {
    if (fetchedAccessToken == null) return;

    const isDisabled = fetchedAccessToken.account?.idTokenClaims?.extension_userIsDisabled as
      | boolean
      | undefined;
    const isValidated = fetchedAccessToken.account?.idTokenClaims?.extension_userIsValidated as
      | boolean
      | undefined;

    // Only navigate if the status actually changed, not on every token refresh
    const statusChanged =
      statusCheckRef.current == null ||
      statusCheckRef.current.isDisabled !== isDisabled ||
      statusCheckRef.current.isValidated !== isValidated;

    statusCheckRef.current = { isDisabled, isValidated };

    if (
      statusChanged &&
      (isDisabled === true || isValidated === false) &&
      window.location.pathname !== '/inactive'
    ) {
      console.log('Status changed - navigating to /inactive');
      navigate('/inactive');
    }
  }, [fetchedAccessToken, navigate]);

  // Safety net: if no access token has arrived shortly after sign-in, retry silent acquisition
  // instead of signing the user out. Only an InteractionRequired error sends them back to B2C.
  useEffect(() => {
    if (!isTokenMissing || authPca == null) return;

    const timeout = setTimeout(() => {
      const account = authPca.getActiveAccount() ?? authPca.getAllAccounts()[0];
      if (account == null) {
        logWarning('No access token after sign-in and no MSAL account to retry with.');
        return;
      }

      logWarning(`No access token ${TOKEN_RETRY_DELAY_MS}ms after sign-in; retrying silently.`);
      const scopes = [import.meta.env.VITE_SCOPE as string];

      authPca
        .acquireTokenSilent({ scopes, account })
        .then((response) => {
          updateAccessToken((prevValue) =>
            prevValue?.accessToken === response.accessToken ? prevValue : response
          );
        })
        .catch(async (error: unknown) => {
          if (error instanceof InteractionRequiredAuthError) {
            trackAuthRedirect('InteractionRequired', {
              error,
              b2cId: account.localAccountId,
              trigger: 'tokenRetry',
            });
            await authPca.acquireTokenRedirect({ scopes, account });
            return;
          }

          logError('No access token after sign-in, and the silent retry failed.', error);
        });
    }, TOKEN_RETRY_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [isTokenMissing, authPca, updateAccessToken]);

  const contextValue = useMemo(
    () => ({
      authResult: fetchedAccessToken!,
      getLatestAuthResult,
      b2cId,
      validated,
      authPca,
      isTokenMissing,
      updateAccessToken,
      updateB2cId,
      updateValidated,
      updateAuthPca,
    }),
    [
      fetchedAccessToken,
      getLatestAuthResult,
      updateAccessToken,
      b2cId,
      validated,
      authPca,
      isTokenMissing,
      updateB2cId,
      updateValidated,
      updateAuthPca,
    ]
  );

  return <TokenContext.Provider value={contextValue}>{children}</TokenContext.Provider>;
};
